<script setup>
import { ref, onMounted } from 'vue';

const props = defineProps({
    postsUrl: { type: String, required: true },
    storeUrl: { type: String, required: true },
});

const emit = defineEmits(['close']);

const posts = ref([]);
const loading = ref(true);
const showForm = ref(false);
const newPost = ref({ content: '', content_type: null, title: '' });
const saving = ref(false);

const contentTypeLabels = {
    linkedin: 'LinkedIn',
    blog: 'Blog',
    instagram: 'Instagram',
    newsletter: 'Newsletter',
    facebook: 'Facebook',
    other: 'Other',
};

onMounted(async () => {
    await loadPosts();
});

const loadPosts = async () => {
    loading.value = true;
    try {
        const res = await fetch(props.postsUrl, {
            headers: { 'Accept': 'application/json' },
        });
        const data = await res.json();
        posts.value = data.data || [];
    } catch (e) {
        console.error('Failed to load historical posts', e);
    } finally {
        loading.value = false;
    }
};

const addPost = async () => {
    if (!newPost.value.content.trim()) return;
    saving.value = true;

    try {
        const res = await fetch(props.storeUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content || '',
            },
            body: JSON.stringify(newPost.value),
        });
        const data = await res.json();
        posts.value.unshift(data);
        newPost.value = { content: '', content_type: null, title: '' };
        showForm.value = false;
    } catch (e) {
        console.error('Failed to save historical post', e);
    } finally {
        saving.value = false;
    }
};

const deletePost = async (post) => {
    try {
        const url = props.postsUrl.replace(/\/$/, '');
        await fetch(`${url}/${post.id}`, {
            method: 'DELETE',
            headers: {
                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content || '',
            },
        });
        posts.value = posts.value.filter(p => p.id !== post.id);
    } catch (e) {
        console.error('Failed to delete historical post', e);
    }
};

const truncate = (text, len = 150) => {
    if (!text) return '';
    return text.length > len ? text.substring(0, len) + '...' : text;
};
</script>

<template>
    <div class="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50">
        <div class="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-[85vh] flex flex-col">
            <!-- Header -->
            <div class="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <div>
                    <h2 class="text-lg font-bold text-gray-800">
                        <mf-translate domainkey="content-creator:historicalPosts.title"></mf-translate>
                    </h2>
                    <p class="text-sm text-gray-500">
                        <mf-translate domainkey="content-creator:historicalPosts.explanation"></mf-translate>
                    </p>
                </div>
                <button-base @click="$emit('close')" style-type="default" size="xs" class="!border-0 !bg-transparent !px-1 !py-1 !text-gray-400">
                    <template #icon>
                        <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
                    </template>
                </button-base>
            </div>

            <!-- Content -->
            <div class="flex-1 overflow-y-auto p-6">
                <!-- Add post button -->
                <button-base
                    v-if="!showForm"
                    @click="showForm = true"
                    style-type="secondary"
                    size="lg"
                    class="w-full gap-2 justify-center mb-4"
                >
                    <template #icon>
                        <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 5v14M5 12h14" /></svg>
                    </template>
                    <mf-translate domainkey="content-creator:historicalPosts.addPost"></mf-translate>
                </button-base>

                <!-- Add post form -->
                <div v-if="showForm" class="bg-gray-50 rounded-lg p-4 mb-4 space-y-3">
                    <input
                        v-model="newPost.title"
                        type="text"
                        class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-aiAgentContentCreator focus:border-aiAgentContentCreator"
                        :placeholder="_mft('content-creator:historicalPosts.titlePlaceholder')"
                    />
                    <textarea
                        v-model="newPost.content"
                        rows="5"
                        class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-aiAgentContentCreator focus:border-aiAgentContentCreator"
                        :placeholder="_mft('content-creator:historicalPosts.contentPlaceholder')"
                    ></textarea>
                    <div class="flex items-center gap-2">
                        <select
                            v-model="newPost.content_type"
                            class="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-aiAgentContentCreator focus:border-aiAgentContentCreator"
                        >
                            <option :value="null">{{ _mft('content-creator:historicalPosts.selectType') }}</option>
                            <option v-for="(label, key) in contentTypeLabels" :key="key" :value="key">{{ label }}</option>
                        </select>
                        <div class="flex-1"></div>
                        <button-default
                            @click="showForm = false"
                            size="sm"
                        >
                            <mf-translate domainkey="content-creator:historicalPosts.cancel"></mf-translate>
                        </button-default>
                        <button-primary
                            @click="addPost"
                            :disabled="!newPost.content.trim() || saving"
                            size="sm"
                        >
                            <mf-translate domainkey="content-creator:historicalPosts.save"></mf-translate>
                        </button-primary>
                    </div>
                </div>

                <!-- Posts list -->
                <div v-if="loading" class="text-center py-8 text-gray-400 text-sm">
                    Loading...
                </div>

                <div v-else-if="posts.length === 0" class="text-center py-8">
                    <p class="text-sm text-gray-400">
                        <mf-translate domainkey="content-creator:historicalPosts.empty"></mf-translate>
                    </p>
                </div>

                <div v-else class="space-y-3">
                    <div
                        v-for="post in posts"
                        :key="post.id"
                        class="bg-white border border-gray-200 rounded-lg p-4 group"
                    >
                        <div class="flex items-start justify-between mb-2">
                            <div class="flex items-center gap-2">
                                <span v-if="post.title" class="text-sm font-medium text-gray-800">{{ post.title }}</span>
                                <span
                                    v-if="post.content_type"
                                    class="px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600"
                                >
                                    {{ contentTypeLabels[post.content_type] || post.content_type }}
                                </span>
                            </div>
                <button-base
                    @click="deletePost(post)"
                    style-type="default"
                    size="xs"
                    class="opacity-0 group-hover:opacity-100 !border-0 !bg-transparent !px-1 !py-1 !text-red-500"
                >
                    <template #icon>
                        <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                        </svg>
                    </template>
                </button-base>
                        </div>
                        <p class="text-xs text-gray-600 whitespace-pre-line">{{ truncate(post.content) }}</p>
                        <span class="text-xs text-gray-400 mt-2 block">{{ new Date(post.created_at).toLocaleDateString() }}</span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
